<template>
  <v-container fluid class="audit-page pa-4 pa-md-6">
    <v-card class="audit-page-header mb-4" elevation="0">
      <v-card-text class="pa-4 pa-md-5">
        <div class="d-flex flex-wrap align-center ga-4">
          <v-avatar color="white" variant="tonal" size="52">
            <v-icon color="white" size="30">mdi-shield-search-outline</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h5 font-weight-bold">Activity Audit Log</h1>
            <p class="text-body-2 audit-header-subtitle mb-0">
              Review each request as one activity, with master and detail changes kept together.
            </p>
          </div>
          <v-spacer />
          <v-chip color="white" variant="outlined" prepend-icon="mdi-database-clock-outline">
            {{ activeTab === "activities" ? activityPagination.total : rawPagination.total }} record{{ (activeTab === "activities" ? activityPagination.total : rawPagination.total) === 1 ? "" : "s" }}
          </v-chip>
          <v-btn
            color="white"
            variant="tonal"
            prepend-icon="mdi-refresh"
            :loading="currentLoading"
            @click="refreshCurrent"
          >
            Refresh
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="audit-section-card mb-4" elevation="0">
      <v-tabs v-model="activeTab" color="primary" show-arrows>
        <v-tab value="activities" prepend-icon="mdi-source-commit">Grouped Activities</v-tab>
        <v-tab value="raw" prepend-icon="mdi-code-json">Raw Changes</v-tab>
      </v-tabs>
      <v-divider />

      <v-card-text class="audit-filter-area">
        <v-row align="center" dense>
          <v-col cols="12" md="5" lg="4">
            <v-text-field
              v-if="activeTab === 'activities'"
              v-model.trim="activityFilters.search"
              label="Search record, ID or route"
              placeholder="e.g. Blue 2 or PO-00025"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @keyup.enter="applyCurrentFilters"
            />
            <v-text-field
              v-else
              v-model.trim="rawFilters.search"
              label="Search record, ID or model"
              placeholder="e.g. Blue 2 or Product"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @keyup.enter="applyCurrentFilters"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-select
              v-if="activeTab === 'activities'"
              v-model="activityFilters.module"
              :items="meta.modules"
              item-title="label"
              item-value="value"
              label="Module"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              :loading="metaLoading"
              :menu-props="menuProps"
            />
            <v-select
              v-else
              v-model="rawFilters.module"
              :items="meta.modules"
              item-title="label"
              item-value="value"
              label="Module"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              :loading="metaLoading"
              :menu-props="menuProps"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-select
              v-if="activeTab === 'activities'"
              v-model="activityFilters.event"
              :items="meta.events"
              item-title="label"
              item-value="value"
              label="Event"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              :loading="metaLoading"
              :menu-props="menuProps"
            />
            <v-select
              v-else
              v-model="rawFilters.event"
              :items="meta.events"
              item-title="label"
              item-value="value"
              label="Event"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              :loading="metaLoading"
              :menu-props="menuProps"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-text-field
              v-if="activeTab === 'activities'"
              v-model="activityFilters.start_date"
              type="date"
              label="From date"
              variant="outlined"
              density="compact"
              hide-details
            />
            <v-text-field
              v-else
              v-model="rawFilters.start_date"
              type="date"
              label="From date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-text-field
              v-if="activeTab === 'activities'"
              v-model="activityFilters.end_date"
              type="date"
              label="To date"
              variant="outlined"
              density="compact"
              hide-details
            />
            <v-text-field
              v-else
              v-model="rawFilters.end_date"
              type="date"
              label="To date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <v-expand-transition>
          <div v-show="advancedOpen" class="audit-advanced-filters mt-4 pa-3 pa-md-4">
            <v-row dense>
              <v-col cols="12" sm="6" lg="3">
                <v-select
                  v-if="activeTab === 'activities'"
                  v-model="activityFilters.company_id"
                  :items="meta.companies"
                  item-title="label"
                  item-value="value"
                  label="Company"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
                <v-select
                  v-else
                  v-model="rawFilters.company_id"
                  :items="meta.companies"
                  item-title="label"
                  item-value="value"
                  label="Company"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-select
                  v-if="activeTab === 'activities'"
                  v-model="activityFilters.branch_id"
                  :items="activityBranches"
                  item-title="label"
                  item-value="value"
                  label="Branch"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
                <v-select
                  v-else
                  v-model="rawFilters.branch_id"
                  :items="rawBranches"
                  item-title="label"
                  item-value="value"
                  label="Branch"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-autocomplete
                  v-if="activeTab === 'activities'"
                  v-model="activityFilters.entity"
                  :items="meta.entities"
                  item-title="label"
                  item-value="value"
                  label="Master entity"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
                <v-autocomplete
                  v-else
                  v-model="rawFilters.auditable_type"
                  :items="meta.entities"
                  item-title="label"
                  item-value="type"
                  label="Changed entity"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-autocomplete
                  v-if="activeTab === 'activities'"
                  v-model="activityFilters.user_id"
                  :items="meta.users"
                  item-title="label"
                  item-value="value"
                  label="Changed by"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
                <v-autocomplete
                  v-else
                  v-model="rawFilters.user_id"
                  :items="meta.users"
                  item-title="label"
                  item-value="value"
                  label="Changed by"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col v-if="activeTab === 'activities'" cols="12" sm="6" lg="3">
                <v-select
                  v-model="activityFilters.source"
                  :items="meta.sources"
                  item-title="label"
                  item-value="value"
                  label="Source"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  :loading="metaLoading"
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col v-if="activeTab === 'activities'" cols="12" sm="6" lg="3">
                <v-select
                  v-model="activityFilters.sort"
                  :items="sortOptions"
                  item-title="label"
                  item-value="value"
                  label="Order"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :menu-props="menuProps"
                />
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-text-field
                  v-if="activeTab === 'activities'"
                  v-model.trim="activityFilters.request_uuid"
                  label="Request UUID"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  prepend-inner-icon="mdi-identifier"
                />
                <v-text-field
                  v-else
                  v-model.trim="rawFilters.request_uuid"
                  label="Request UUID"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  prepend-inner-icon="mdi-identifier"
                />
              </v-col>

              <v-col v-if="activeTab === 'raw'" cols="12" sm="6" lg="3">
                <v-text-field
                  v-model.trim="rawFilters.auditable_id"
                  label="Record ID"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  prepend-inner-icon="mdi-pound"
                />
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

        <div class="d-flex flex-wrap align-center ga-2 mt-4">
          <v-btn color="primary" prepend-icon="mdi-filter-check-outline" :loading="currentLoading" @click="applyCurrentFilters">
            Apply filters
          </v-btn>
          <v-btn color="blue-grey" variant="tonal" prepend-icon="mdi-filter-off-outline" @click="resetCurrentFilters">
            Reset
          </v-btn>
          <v-btn variant="text" color="primary" :append-icon="advancedOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="advancedOpen = !advancedOpen">
            More filters
          </v-btn>
          <v-chip v-if="currentFilterCount" size="small" color="primary" variant="tonal">
            {{ currentFilterCount }} active
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-if="metaError" type="warning" variant="tonal" class="mb-4" closable @click:close="metaError = ''">
      {{ metaError }} Filters that depend on reference data may be unavailable.
      <template #append><v-btn size="small" variant="text" @click="loadMeta">Retry</v-btn></template>
    </v-alert>

    <v-card v-if="activeTab === 'activities'" class="audit-section-card" elevation="0">
      <v-card-title class="d-flex flex-wrap align-center ga-2 px-4 py-3">
        <v-icon color="primary">mdi-source-commit</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Grouped activities</span>
        <span class="text-caption text-medium-emphasis">One row per request or transaction</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ paginationLabel(activityPagination) }}</span>
      </v-card-title>
      <v-divider />
      <v-alert v-if="activityError" type="error" variant="tonal" class="ma-4">
        {{ activityError }}
        <template #append><v-btn size="small" variant="text" @click="loadActivities">Retry</v-btn></template>
      </v-alert>

      <v-data-table-server
        v-model:page="activityPage"
        v-model:items-per-page="activityPerPage"
        :headers="activityHeaders"
        :items="activities"
        :items-length="activityPagination.total"
        :items-per-page-options="pageSizeOptions"
        :loading="activityLoading"
        item-value="uuid"
        density="comfortable"
        hover
        class="audit-table"
        @update:options="onActivityOptions"
      >
        <template #item.last_event_at="{ item }">
          <div class="audit-date-cell">
            <strong>{{ formatDate(itemValue(item, "last_event_at")) }}</strong>
            <span>{{ formatTime(itemValue(item, "last_event_at")) }}</span>
          </div>
        </template>
        <template #item.module="{ item }">
          <v-chip size="small" color="indigo" variant="tonal">{{ itemValue(item, "module_label") || humanize(itemValue(item, "module")) }}</v-chip>
        </template>
        <template #item.record="{ item }">
          <div class="audit-record-cell">
            <span class="text-caption text-primary font-weight-medium">{{ itemValue(item, "entity_label") }}</span>
            <strong>{{ itemValue(item, "root_label") || `${itemValue(item, "entity_label")} #${itemValue(item, "root_id")}` }}</strong>
            <span class="text-caption text-medium-emphasis">ID {{ itemValue(item, "root_id") ?? "—" }}</span>
          </div>
        </template>
        <template #item.events="{ item }">
          <div class="d-flex flex-wrap ga-1">
            <v-chip
              v-for="event in itemValue(item, 'events') || []"
              :key="event"
              :color="eventColor(event)"
              size="x-small"
              variant="tonal"
            >
              {{ humanize(event) }} {{ itemValue(item, "event_counts")?.[event] || "" }}
            </v-chip>
          </div>
        </template>
        <template #item.user="{ item }">
          <div class="audit-user-cell">
            <v-avatar size="28" color="blue-grey-lighten-4"><v-icon size="17">mdi-account-outline</v-icon></v-avatar>
            <span>{{ itemValue(item, "user")?.label || "System" }}</span>
          </div>
        </template>
        <template #item.scope="{ item }">
          <div class="audit-scope-cell">
            <span><v-icon size="14">mdi-office-building-outline</v-icon>{{ itemValue(item, "company")?.label || "—" }}</span>
            <span><v-icon size="14">mdi-source-branch</v-icon>{{ itemValue(item, "branch")?.label || "—" }}</span>
          </div>
        </template>
        <template #item.changes_count="{ item }">
          <v-chip size="small" color="blue-grey" variant="outlined">{{ itemValue(item, "changes_count") }}</v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            color="primary"
            variant="tonal"
            size="small"
            prepend-icon="mdi-eye-outline"
            @click.stop="openActivity(itemValue(item, 'uuid'))"
          >
            View
          </v-btn>
        </template>
        <template #loading><v-skeleton-loader type="table-row@6" /></template>
        <template #no-data>
          <div class="audit-empty-state pa-8">
            <v-icon size="52" color="blue-grey-lighten-2">mdi-text-box-search-outline</v-icon>
            <h3 class="text-subtitle-1 mt-2">No activities found</h3>
            <p class="text-body-2 text-medium-emphasis">Try changing the filters or date range.</p>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <v-card v-else class="audit-section-card" elevation="0">
      <v-card-title class="d-flex flex-wrap align-center ga-2 px-4 py-3">
        <v-icon color="primary">mdi-code-json</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Raw changes</span>
        <span class="text-caption text-medium-emphasis">One row per changed database model</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ paginationLabel(rawPagination) }}</span>
      </v-card-title>
      <v-divider />
      <v-alert v-if="rawError" type="error" variant="tonal" class="ma-4">
        {{ rawError }}
        <template #append><v-btn size="small" variant="text" @click="loadRawAudits">Retry</v-btn></template>
      </v-alert>

      <v-data-table-server
        v-model:page="rawPage"
        v-model:items-per-page="rawPerPage"
        :headers="rawHeaders"
        :items="rawAudits"
        :items-length="rawPagination.total"
        :items-per-page-options="pageSizeOptions"
        :loading="rawLoading"
        item-value="id"
        density="comfortable"
        hover
        class="audit-table"
        @update:options="onRawOptions"
      >
        <template #item.created_at="{ item }">
          <div class="audit-date-cell">
            <strong>{{ formatDate(itemValue(item, "created_at")) }}</strong>
            <span>{{ formatTime(itemValue(item, "created_at")) }}</span>
          </div>
        </template>
        <template #item.module="{ item }">
          <v-chip size="small" color="indigo" variant="tonal">{{ humanize(itemValue(item, "module")) }}</v-chip>
        </template>
        <template #item.record="{ item }">
          <div class="audit-record-cell">
            <span class="text-caption text-primary font-weight-medium">{{ itemValue(item, "entity_label") }}</span>
            <strong>{{ itemValue(item, "record_label") }}</strong>
            <span class="text-caption text-medium-emphasis">ID {{ itemValue(item, "record_id") ?? "—" }}</span>
          </div>
        </template>
        <template #item.event="{ item }">
          <v-chip :color="eventColor(itemValue(item, 'event'))" size="small" variant="tonal">
            {{ humanize(itemValue(item, "event")) }}
          </v-chip>
        </template>
        <template #item.field_count="{ item }">
          <v-chip size="small" color="blue-grey" variant="outlined">{{ itemValue(item, "changes")?.length || 0 }}</v-chip>
        </template>
        <template #item.user_id="{ item }">
          <span>{{ userLabel(itemValue(item, "user_id")) }}</span>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            :disabled="!itemValue(item, 'activity_uuid')"
            color="primary"
            variant="tonal"
            size="small"
            prepend-icon="mdi-eye-outline"
            @click.stop="openActivity(itemValue(item, 'activity_uuid'))"
          >
            Activity
          </v-btn>
        </template>
        <template #loading><v-skeleton-loader type="table-row@6" /></template>
        <template #no-data>
          <div class="audit-empty-state pa-8">
            <v-icon size="52" color="blue-grey-lighten-2">mdi-code-json</v-icon>
            <h3 class="text-subtitle-1 mt-2">No raw changes found</h3>
            <p class="text-body-2 text-medium-emphasis">Try changing the filters or date range.</p>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <AuditActivityDetail
      v-model="detailDialog"
      :detail="activityDetail"
      :loading="detailLoading"
      :error="detailError"
    />
  </v-container>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import AuditActivityDetail from "@/components/module/auditLog/AuditActivityDetail.vue";
import auditLogApi from "@/services/auditLogApi";

const menuProps = { contentClass: "my-custom-dropdown" };
const pageSizeOptions = [10, 25, 50, 100];
const sortOptions = [
  { label: "Newest first", value: "newest" },
  { label: "Oldest first", value: "oldest" },
];

const emptyActivityFilters = () => ({
  search: "",
  company_id: null,
  branch_id: null,
  module: null,
  entity: null,
  event: null,
  user_id: null,
  source: null,
  start_date: "",
  end_date: "",
  request_uuid: "",
  sort: "newest",
});

const emptyRawFilters = () => ({
  search: "",
  company_id: null,
  branch_id: null,
  module: null,
  event: null,
  auditable_type: null,
  auditable_id: "",
  user_id: null,
  start_date: "",
  end_date: "",
  request_uuid: "",
});

const emptyPagination = () => ({ current_page: 1, last_page: 1, per_page: 25, total: 0, from: null, to: null });

const activeTab = ref("activities");
const advancedOpen = ref(false);
const metaLoading = ref(false);
const metaError = ref("");
const meta = reactive({ modules: [], entities: [], events: [], sources: [], users: [], companies: [], branches: [] });

const activityFilters = reactive(emptyActivityFilters());
const activityPage = ref(1);
const activityPerPage = ref(25);
const activities = ref([]);
const activityPagination = reactive(emptyPagination());
const activityLoading = ref(false);
const activityError = ref("");
let activityRequest = 0;

const rawFilters = reactive(emptyRawFilters());
const rawPage = ref(1);
const rawPerPage = ref(25);
const rawAudits = ref([]);
const rawPagination = reactive(emptyPagination());
const rawLoading = ref(false);
const rawError = ref("");
const rawLoaded = ref(false);
let rawRequest = 0;

const detailDialog = ref(false);
const detailLoading = ref(false);
const detailError = ref("");
const activityDetail = ref(null);
let detailRequest = 0;

const activityHeaders = [
  { title: "Last change", key: "last_event_at", sortable: false, width: 155 },
  { title: "Module", key: "module", sortable: false, width: 130 },
  { title: "Record", key: "record", sortable: false, minWidth: 230 },
  { title: "Events", key: "events", sortable: false, minWidth: 170 },
  { title: "Changed by", key: "user", sortable: false, minWidth: 160 },
  { title: "Scope", key: "scope", sortable: false, minWidth: 190 },
  { title: "Changes", key: "changes_count", sortable: false, align: "center", width: 90 },
  { title: "", key: "actions", sortable: false, align: "end", width: 100 },
];

const rawHeaders = [
  { title: "Changed at", key: "created_at", sortable: false, width: 155 },
  { title: "Module", key: "module", sortable: false, width: 130 },
  { title: "Record", key: "record", sortable: false, minWidth: 260 },
  { title: "Event", key: "event", sortable: false, width: 120 },
  { title: "Fields", key: "field_count", sortable: false, align: "center", width: 80 },
  { title: "Changed by", key: "user_id", sortable: false, minWidth: 150 },
  { title: "", key: "actions", sortable: false, align: "end", width: 115 },
];

const activityBranches = computed(() => filterBranches(activityFilters.company_id));
const rawBranches = computed(() => filterBranches(rawFilters.company_id));
const currentLoading = computed(() => activeTab.value === "activities" ? activityLoading.value : rawLoading.value);
const currentFilterCount = computed(() => countFilters(activeTab.value === "activities" ? activityFilters : rawFilters));

const eventColors = { created: "success", updated: "primary", deleted: "error", restored: "warning" };
const eventColor = (event) => eventColors[event] || "blue-grey";
const humanize = (value) => String(value || "Unknown").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

function filterBranches(companyId) {
  if (!companyId) return meta.branches;
  return meta.branches.filter((branch) => Number(branch.company_id) === Number(companyId));
}

function countFilters(filters) {
  return Object.entries(filters).filter(([key, value]) => {
    if (key === "sort" && value === "newest") return false;
    return value !== null && value !== undefined && value !== "";
  }).length;
}

function itemValue(item, key) {
  return (item?.raw || item)?.[key];
}

function apiError(exception, fallback) {
  const response = exception?.response?.data;
  const errors = response?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return response?.message || exception?.message || fallback;
}

function validDateRange(filters) {
  if (filters.start_date && filters.end_date && filters.end_date < filters.start_date) {
    toast.error("To date must be the same as or later than From date.");
    return false;
  }
  return true;
}

async function loadMeta() {
  metaLoading.value = true;
  metaError.value = "";
  try {
    const response = await auditLogApi.meta();
    const data = response.data?.data || {};
    Object.assign(meta, {
      modules: data.modules || [],
      entities: data.entities || [],
      events: data.events || [],
      sources: data.sources || [],
      users: data.users || [],
      companies: data.companies || [],
      branches: data.branches || [],
    });
    const defaultPerPage = Number(data.defaults?.per_page);
    if ([10, 25, 50, 100].includes(defaultPerPage)) {
      activityPerPage.value = defaultPerPage;
      rawPerPage.value = defaultPerPage;
    }
  } catch (exception) {
    metaError.value = apiError(exception, "Unable to load audit filter options.");
  } finally {
    metaLoading.value = false;
  }
}

async function loadActivities() {
  if (!validDateRange(activityFilters)) return;
  const request = ++activityRequest;
  activityLoading.value = true;
  activityError.value = "";
  try {
    const response = await auditLogApi.activities({
      ...activityFilters,
      page: activityPage.value,
      per_page: activityPerPage.value,
    });
    if (request !== activityRequest) return;
    activities.value = response.data?.data || [];
    Object.assign(activityPagination, emptyPagination(), response.data?.pagination || {});
  } catch (exception) {
    if (request !== activityRequest) return;
    activities.value = [];
    Object.assign(activityPagination, emptyPagination());
    activityError.value = apiError(exception, "Unable to load audit activities.");
  } finally {
    if (request === activityRequest) activityLoading.value = false;
  }
}

async function loadRawAudits() {
  if (!validDateRange(rawFilters)) return;
  const request = ++rawRequest;
  rawLoading.value = true;
  rawError.value = "";
  try {
    const response = await auditLogApi.rawAudits({
      ...rawFilters,
      page: rawPage.value,
      per_page: rawPerPage.value,
    });
    if (request !== rawRequest) return;
    rawAudits.value = response.data?.data || [];
    Object.assign(rawPagination, emptyPagination(), response.data?.pagination || {});
    rawLoaded.value = true;
  } catch (exception) {
    if (request !== rawRequest) return;
    rawAudits.value = [];
    Object.assign(rawPagination, emptyPagination());
    rawError.value = apiError(exception, "Unable to load raw audit changes.");
  } finally {
    if (request === rawRequest) rawLoading.value = false;
  }
}

function onActivityOptions(options) {
  const pageChanged = activityPage.value !== options.page;
  const sizeChanged = activityPerPage.value !== options.itemsPerPage;
  activityPage.value = options.page;
  activityPerPage.value = options.itemsPerPage;
  if (sizeChanged && !pageChanged) activityPage.value = 1;
  loadActivities();
}

function onRawOptions(options) {
  const pageChanged = rawPage.value !== options.page;
  const sizeChanged = rawPerPage.value !== options.itemsPerPage;
  rawPage.value = options.page;
  rawPerPage.value = options.itemsPerPage;
  if (sizeChanged && !pageChanged) rawPage.value = 1;
  loadRawAudits();
}

function applyCurrentFilters() {
  if (activeTab.value === "activities") {
    activityPage.value = 1;
    loadActivities();
  } else {
    rawPage.value = 1;
    loadRawAudits();
  }
}

function resetCurrentFilters() {
  if (activeTab.value === "activities") {
    Object.assign(activityFilters, emptyActivityFilters());
    activityPage.value = 1;
    loadActivities();
  } else {
    Object.assign(rawFilters, emptyRawFilters());
    rawPage.value = 1;
    loadRawAudits();
  }
}

function refreshCurrent() {
  if (activeTab.value === "activities") loadActivities();
  else loadRawAudits();
}

async function openActivity(uuid) {
  if (!uuid) return;
  const request = ++detailRequest;
  detailDialog.value = true;
  detailLoading.value = true;
  detailError.value = "";
  activityDetail.value = null;
  try {
    const response = await auditLogApi.activity(uuid);
    if (request === detailRequest) activityDetail.value = response.data?.data || null;
  } catch (exception) {
    if (request === detailRequest) detailError.value = apiError(exception, "Unable to load this audit activity.");
  } finally {
    if (request === detailRequest) detailLoading.value = false;
  }
}

function userLabel(userId) {
  if (!userId) return "System";
  return meta.users.find((user) => Number(user.value) === Number(userId))?.label || `User #${userId}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(date);
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(date);
}

function paginationLabel(pagination) {
  if (!pagination.total) return "0 records";
  return `${pagination.from || 1}–${pagination.to || pagination.total} of ${pagination.total}`;
}

watch(() => activityFilters.company_id, (companyId) => {
  if (activityFilters.branch_id && !filterBranches(companyId).some((branch) => Number(branch.value) === Number(activityFilters.branch_id))) {
    activityFilters.branch_id = null;
  }
});

watch(() => rawFilters.company_id, (companyId) => {
  if (rawFilters.branch_id && !filterBranches(companyId).some((branch) => Number(branch.value) === Number(rawFilters.branch_id))) {
    rawFilters.branch_id = null;
  }
});

watch(activeTab, (tab) => {
  if (tab === "raw" && !rawLoaded.value) loadRawAudits();
});

onMounted(() => {
  loadMeta();
  loadActivities();
});

onBeforeUnmount(() => {
  activityRequest += 1;
  rawRequest += 1;
  detailRequest += 1;
});
</script>

<style scoped>
.audit-page {
  min-height: calc(100vh - 64px);
  background: #f4f7f8;
}

.audit-page-header {
  color: #fff;
  background: linear-gradient(135deg, #213b55 0%, #2f6377 60%, #387d83 100%);
  border-radius: 16px;
}

.audit-header-subtitle {
  color: rgba(255, 255, 255, 0.78);
}

.audit-section-card {
  overflow: hidden;
  border: 1px solid #dde5e9;
  border-radius: 14px;
}

.audit-filter-area {
  background: #fbfcfc;
}

.audit-advanced-filters {
  background: #f3f6f7;
  border: 1px dashed #ced9de;
  border-radius: 12px;
}

.audit-table :deep(.v-data-table__th) {
  color: #3c515b;
  font-weight: 700;
  background: #f5f7f8;
  white-space: nowrap;
}

.audit-table :deep(.v-data-table__tr:hover > td) {
  background: #f6fafb !important;
}

.audit-date-cell,
.audit-record-cell,
.audit-scope-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-block: 5px;
}

.audit-date-cell strong {
  color: #334952;
  font-size: 0.82rem;
}

.audit-date-cell span {
  color: #78868d;
  font-size: 0.74rem;
}

.audit-record-cell strong {
  max-width: 300px;
  color: #2c414a;
  overflow-wrap: anywhere;
}

.audit-user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audit-scope-cell span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #52646c;
  font-size: 0.78rem;
}

.audit-empty-state {
  color: #5e7078;
  text-align: center;
}

@media (max-width: 700px) {
  .audit-page {
    padding: 12px !important;
  }

  .audit-page-header {
    border-radius: 12px;
  }
}
</style>
